import React from 'react';
import { ReactComponent as HexIcon } from '@elementary/common-ui/assets/16x16/hex.svg';
import { ReactComponent as LightdashIcon } from '@elementary/common-ui/assets/16x16/lightdash.svg';
import { ReactComponent as LookerIcon } from '@elementary/common-ui/assets/16x16/looker.svg';
import { ReactComponent as PowerBiIcon } from '@elementary/common-ui/assets/16x16/power-bi.svg';
import { ReactComponent as SigmaIcon } from '@elementary/common-ui/assets/16x16/sigma.svg';
import { ReactComponent as TableauIcon } from '@elementary/common-ui/assets/16x16/tableau.svg';
import { ReactComponent as ThoughtSpotIcon } from '@elementary/common-ui/assets/16x16/thoughtspot.svg';
import { Theme } from '@mui/material';
import { FetchBiIntegrationApi } from 'shared/types';
import multipleDestinationsRenderer from '../../shared/multipleDestinationsRenderer';

export const platformIconMap: Record<string, React.ReactNode> = {
  Tableau: <TableauIcon />,
  Looker: <LookerIcon />,
  PowerBI: <PowerBiIcon />,
  Sigma: <SigmaIcon />,
  ThoughtSpot: <ThoughtSpotIcon />,
  Hex: <HexIcon />,
  Lightdash: <LightdashIcon />,
};

export type BiMultipleDestinationsRendererProps = {
  theme: Theme;
  biIntegrations: FetchBiIntegrationApi[];
  onChange: (method: string, id: string) => void;
};

const GENERIC_BI_SYNC_ERROR_TOOLTIP =
  'Sync failed. Please check your connection settings, or contact support if the issue persists.';

const getDisplayStatus = (
  syncStatus: FetchBiIntegrationApi['last_sync_status'],
) => {
  switch (syncStatus) {
    case 'error':
      return 'error';
    case 'running':
      return 'syncing';
    default:
      return 'connected';
  }
};

const biMultipleDestinationsRenderer = ({
  theme,
  biIntegrations,
  onChange,
}: BiMultipleDestinationsRendererProps): React.ReactNode =>
  multipleDestinationsRenderer({
    integrations: biIntegrations.map((biIntegration) => {
      const status = getDisplayStatus(biIntegration.last_sync_status);

      return {
        id: biIntegration.id,
        icon: platformIconMap[biIntegration.bi_type],
        name: biIntegration.name,
        status,
        statusTooltip:
          status === 'error'
            ? biIntegration.last_sync_error_message ||
              GENERIC_BI_SYNC_ERROR_TOOLTIP
            : null,
        last_synced_at: biIntegration.last_synced_at,
      };
    }),
    onChange,
    options: [
      { value: 'sync', label: 'Sync now' },
      { value: 'update', label: 'Edit connection' },
      {
        value: 'delete',
        label: 'Delete connection',
        color: theme.palette.status.error.main,
      },
    ],
  });

export default biMultipleDestinationsRenderer;
